import { Avatar, Box, Flex, Image, Text } from '@chakra-ui/react';
import moment from 'moment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import messageBubbleTriangle from 'assets/img/polygon/message-bubble-triangle-white.svg';
import { Color } from 'presentation/theme/colorScheme';
import { FiMoreHorizontal } from 'react-icons/fi';
import AppTooltip from 'presentation/components/shared/tooltip/AppTooltip';
import ContextMenu from 'presentation/components/shared/contextMenu/ContextMenu';
import { ContextMenuItemData } from 'presentation/components/shared/contextMenu/ContextMenuItem';
import editCommentIcon from 'assets/img/icons/edit-comment.svg';
import deleteCommentIcon from 'assets/img/icons/delete-comment.svg';
import { createPortal } from 'react-dom';
import styles from './styles.module.scss';
import classNames from 'classnames';

interface Props {
  comment: CommentListItem;
  onDeleteClicked: () => void;
  onEditClicked: () => void;
}

const CommentListItem: FC<Props> = ({ comment, onDeleteClicked, onEditClicked }) => {
  const { t } = useTranslation();

  const tooltipId = `comment-tooltip-${comment.id}`;

  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);

  const contextMenuItems: ContextMenuItemData[] = [];
  if (comment.isEditable) {
    contextMenuItems.push({
      text: t('edit'),
      icon: editCommentIcon,
      onClick: () => {
        onEditClicked();
        setIsTooltipOpen(false);
      },
    });
  }
  if (comment.isRemovable) {
    contextMenuItems.push({
      text: t('delete'),
      icon: deleteCommentIcon,
      onClick: () => {
        onDeleteClicked();
        setIsTooltipOpen(false);
      },
      textColor: Color.Danger500,
    });
  }

  const bubble = (
    <Box pos="relative" flex="1">
      <Box
        pos="relative"
        borderRadius="16px"
        bgColor="#fff"
        py="15px"
        px="20px"
        className={styles.bubble}
      >
        <Text fontSize="18px" fontWeight="bold" mb="10px">
          {comment.author.fullName}
        </Text>
        <Text fontSize="18px" mb="10px" wordBreak="break-all">
          {comment.text}
        </Text>
        <Flex>
          {comment.createdAt.getTime() !== comment.updatedAt.getTime() && (
            <Text fontSize="12px" mr="5px" alignSelf="end">
              {t('commentEdited')}
            </Text>
          )}
          <Text fontSize="14px">{moment(comment.createdAt).format('LT')}</Text>
          {(comment.isEditable || comment.isRemovable) && (
            <>
              {createPortal(
                <AppTooltip
                  id={tooltipId}
                  clickable
                  isOpen={isTooltipOpen}
                  setIsOpen={setIsTooltipOpen}
                  style={{
                    padding: '0',
                  }}
                  noArrow
                >
                  <ContextMenu items={contextMenuItems} />
                </AppTooltip>,
                document.body
              )}
              <Box ml="10px">
                <FiMoreHorizontal
                  className={classNames(styles.moreButton, {
                    [styles.moreButtonActive]: isTooltipOpen,
                  })}
                  color="#BEBEBE"
                  cursor="pointer"
                  data-tooltip-id={tooltipId}
                />
              </Box>
            </>
          )}
        </Flex>
      </Box>
      <Image
        src={messageBubbleTriangle}
        pos="absolute"
        w="30px"
        bottom="0"
        right={comment.isSelf ? '-10px' : ''}
        left={comment.isSelf ? '' : '-10px'}
      />
    </Box>
  );

  const avatar = (
    <Avatar
      src={comment.author.photo.previewUrl ?? comment.author.photo.url}
      w="40px"
      h="40px"
      mb="10px"
      ignoreFallback
      bgColor={Color.Shade200}
    />
  );

  return comment.isSelf ? (
    <Flex alignItems="end" w="full">
      {bubble}
      <Box w="6px" />
      {avatar}
    </Flex>
  ) : (
    <Flex alignItems="end" w="full">
      {avatar}
      <Box w="6px" />
      {bubble}
    </Flex>
  );
};

export default CommentListItem;
